import FieldsSelectionMultipleRechercheForm from '../components/FieldsSelectionMultipleRechercheForm'
import { Field } from 'react-final-form'
import React, { CSSProperties, Dispatch, SetStateAction, useContext } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { objectGetKeys } from '../../../utils/ieCompatibility'
import FormNumberFormatField from '../../fields/FormNumberFormatField'
import { FormTypeEnum } from '../../fields/enum/formTypeEnum'
import styles from '../../../styles'
import Link from '../../link/Link'
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined'
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined'
import EuroOutlinedIcon from '@mui/icons-material/EuroOutlined'
import AddOutlinedIcon from '@mui/icons-material/AddOutlined'
import { ResponsiveContext } from '../../../utils/context/ResponsiveContext'
import SvgEspacesVerts from '../../../icons/prestations/EspacesVerts'
import SvgTerrasse from '../../../icons/prestations/Terrasse'
import SvgLoggia from '../../../icons/prestations/Loggia'
import SvgPossibiliteParking from '../../../icons/prestations/PossibiliteParking'
import SvgGardien from '../../../icons/prestations/Gardien'
import SvgLocalVelos from '../../../icons/prestations/LocalVelos'
import SvgAscenseur from '../../../icons/prestations/Ascenseur'
import SvgCuisine from '../../../icons/prestations/Cuisine'
import { FormulaireRechercheModel } from '../../../modules/resultats-recherche/types/formulaireRechercheModel'
import SvgBalcon from '../../../icons/prestations/Balcon'
import SvgCave from '../../../icons/prestations/Cave'
import SvgJardinPrivatif from '../../../icons/prestations/JardinPrivatif'
import { Grid, Theme, useTheme } from '@mui/material'
import { RechercheContext } from '../../../modules/resultats-recherche/utils/RechercheContext'
import {
	biensTypeAppartement,
	budgetEnum,
	champsMenuRechercheEnum,
	etageEnum,
	localisationRechercheEnum,
	nombreChambreEnum,
	parkingEnum,
	prestationsEnum,
	rechercheAvanceeEnum,
	surfaceEnum,
	typeBienRechercheEnum
} from '../../../modules/resultats-recherche/enums/champsMenuRecherche'
import CheckBoxField from '../../fields/CheckBoxField'
import FormTextFieldInline from '../../fields/FormTextFieldInline'

const styleLabel = (theme: Theme) => ({
	...styles(theme).text21,
	fontSize: 18,
	lineHeight: '25px',
	margin: 0,
	marginBottom: 20
})

export const gestionChampsFormulaire = (
	typeChamp: champsMenuRechercheEnum,
	voirPlusPrestation: boolean,
	setVoirPlusPrestation: Dispatch<SetStateAction<boolean>>,
	formValues: FormulaireRechercheModel,
	voirPlusParking: boolean,
	setVoirPlusParking: Dispatch<SetStateAction<boolean>>
) => {
	switch (typeChamp) {
		case champsMenuRechercheEnum.location:
			return ChampsLocation(typeChamp)
		case champsMenuRechercheEnum.typeBien:
			return ChampsTypeBien(typeChamp)
		case champsMenuRechercheEnum.budget:
			return ChampsBudget(typeChamp)
		case champsMenuRechercheEnum.rechercheAvancee:
			return ChampsRechercheAvancee(
				typeChamp,
				voirPlusPrestation,
				setVoirPlusPrestation,
				formValues,
				voirPlusParking,
				setVoirPlusParking
			)
	}
}

export const ChampsLocation = (typeChamp: champsMenuRechercheEnum) => {
	const intl = useIntl()

	return (
		<Field
			name={typeChamp}
			component={FieldsSelectionMultipleRechercheForm}
			id={typeChamp}
			label={
				intl.formatMessage(
					{
						id: `resultatsRecherche.menu.${typeChamp}.label`,
						defaultMessage: 'Où souhaitez-vous chercher votre bien ?',
						description: 'Label du champ de sélection de localisation'
					}
				)
			}
			options={
				objectGetKeys(localisationRechercheEnum).map(
					(localisation) => ({
						label: intl.formatMessage({
							id: `resultatsRecherche.menu.${typeChamp}.${localisation}`,
							defaultMessage: 'Paris',
							description: 'Label des options de localisation'
						}),
						value: localisation
					})
				)
			}
		/>
	)
}

export const ChampsTypeBien = (typeChamp: champsMenuRechercheEnum) => {
	const intl = useIntl()

	return (
		<Field
			name={typeChamp}
			component={FieldsSelectionMultipleRechercheForm}
			id={typeChamp}
			label={
				intl.formatMessage(
					{
						id: `resultatsRecherche.menu.${typeChamp}.label`,
						defaultMessage: 'Quel est votre projet de location ?',
						description: 'Label du champ de sélection de type de bien'
					}
				)
			}
			options={
				objectGetKeys(typeBienRechercheEnum).map(
					(typeBien) => ({
						label: intl.formatMessage({
							id: `resultatsRecherche.menu.${typeChamp}.${typeBien}`,
							defaultMessage: 'Studio',
							description: 'Label des options du type de bien'
						}),
						value: typeBien
					})
				)
			}
		/>
	)
}

export const ChampsBudget = (typeChamp: champsMenuRechercheEnum) => {
	const intl = useIntl()
	const theme = useTheme()

	return (
		<Grid container item xs={12}>
			<Grid
				container
				item
				xs={12}
				style={styleLabel(theme)}
			>
				<FormattedMessage
					id={`resultatsRecherche.menu.${typeChamp}.label`}
					defaultMessage="Pour quel budget ?"
					description="Label du bloc de définition du budget"
				/>
			</Grid>
			<Grid container item xs={12} spacing={2}>
				<Grid container item xs={6}>
					<Field
						name={`${typeChamp}.${budgetEnum.min}`}
						component={FormNumberFormatField}
						id={`${typeChamp}.${budgetEnum.min}`}
						label={
							intl.formatMessage(
								{
									id: `resultatsRecherche.menu.${typeChamp}.${budgetEnum.min}.label`,
									defaultMessage: 'Min',
									description: 'Label du montant minimum'
								}
							)
						}
						maxLength={6}
						allowNegative={false}
						decimalScale={0}
						formType={FormTypeEnum.nombreRecherche}
					/>
				</Grid>

				<Grid container item xs={6}>
					<Field
						name={`${typeChamp}.${budgetEnum.max}`}
						component={FormNumberFormatField}
						id={`${typeChamp}.${budgetEnum.max}`}
						label={
							intl.formatMessage(
								{
									id: `resultatsRecherche.menu.${typeChamp}.${budgetEnum.max}.label`,
									defaultMessage: 'Max',
									description: 'Label du montant maximum'
								}
							)
						}
						maxLength={6}
						allowNegative={false}
						decimalScale={0}
						formType={FormTypeEnum.nombreRecherche}
					/>
				</Grid>
			</Grid>
		</Grid>
	)
}

export const ChampsRechercheAvancee = (
	typeChamp: champsMenuRechercheEnum,
	voirPlusPrestation: boolean,
	setVoirPlusPrestation: Dispatch<SetStateAction<boolean>>,
	formValues: FormulaireRechercheModel,
	voirPlusParking: boolean,
	setVoirPlusParking: Dispatch<SetStateAction<boolean>>
) => {
	const intl = useIntl()
	const theme = useTheme()
	const { isMenuLargeScreen } = useContext(ResponsiveContext)
	const espaceEntreBlocs: CSSProperties = { marginTop: isMenuLargeScreen ? 24 : 20 }
	const espacePrestations: CSSProperties = { marginTop: isMenuLargeScreen ? 3 : 5 }
	const { filtres } = useContext(RechercheContext)

	const selectionSVG = (prestation: prestationsEnum) => {
		const color = theme.palette.green.main

		switch (prestation) {
			case prestationsEnum.balcon:
				return <SvgBalcon color={color} width={35} />

			case prestationsEnum.terrasse:
				return <SvgTerrasse color={color} />

			case prestationsEnum.loggia:
				return <SvgLoggia color={color} />

			case prestationsEnum.jardinPrivatif:
				return <SvgJardinPrivatif color={color} />

			case prestationsEnum.gardien:
				return <SvgGardien color={color} />

			case prestationsEnum.localVelos:
				return <SvgLocalVelos color={color} />

			case prestationsEnum.espacesVerts:
				return <SvgEspacesVerts color={color} />

			case prestationsEnum.ascenseur:
				return <SvgAscenseur color={color} />

			case prestationsEnum.cave:
				return <SvgCave color={color} />

			case prestationsEnum.possibiliteParking:
				return <SvgPossibiliteParking color={color} />

			case prestationsEnum.cuisineSemiEquipee:
			case prestationsEnum.cuisineOuverte:
			case prestationsEnum.cuisineSeparee:
				return <SvgCuisine color={color} />

			default:
				return <SvgBalcon color={color} />

		}
	}

	const rechercheAvanceGestionDisabled = (name: string) => {
		if (name.split('.').includes(rechercheAvanceeEnum.surface) && filtres[champsMenuRechercheEnum.typeBien].some((type) => !biensTypeAppartement.includes(type))) {
			return true
		} else if (name.split('.').includes(rechercheAvanceeEnum.dernierEtage) && filtres[champsMenuRechercheEnum.typeBien].some((type) => !biensTypeAppartement.includes(type))) {
			return true
		} else if (name.split('.').includes(rechercheAvanceeEnum.meuble) && filtres[champsMenuRechercheEnum.typeBien].some((type) => !biensTypeAppartement.includes(type))) {
			return true
		} else if ((name.split('.').includes(champsMenuRechercheEnum.rechercheAvancee) && !name.split('.').includes(rechercheAvanceeEnum.parking)) && filtres[champsMenuRechercheEnum.rechercheAvancee][rechercheAvanceeEnum.parking].length) {
			return true
		}
	}

	return (
		<Grid container item xs={12}>
			{/* Bloc "Appartement meublé ?" */}
			<Grid container item xs={12}>
				<Grid container item xs={12} style={styleLabel(theme)}>
					<FormattedMessage
						id={`resultatsRecherche.menu.${typeChamp}.${rechercheAvanceeEnum.meuble}.label`}
						defaultMessage="Appartement meublé ?"
						description="Label Appartement meublé"
					/>
				</Grid>
				<Field
					name={`${typeChamp}.${rechercheAvanceeEnum.meuble}`}
					component={CheckBoxField}
					type="checkbox"
					disabled={rechercheAvanceGestionDisabled(`${typeChamp}.${rechercheAvanceeEnum.meuble}`)}
					label={
						intl.formatMessage(
							{
								id: `resultatsRecherche.menu.${typeChamp}.${rechercheAvanceeEnum.meuble}.option`,
								defaultMessage: 'Meublé',
								description: 'Checkbox pour le choix d\'un appartement meublé'
							}
						)
					}
					textStyle="text2"
				/>
			</Grid>

			{/* Bloc "Réference" */}
			<Grid container item xs={12} style={espaceEntreBlocs}>
				{/* Titre du champ "Réference" */}
				<Grid
					container
					item
					xs={12}
					style={styleLabel(theme)}
				>
					<FormattedMessage
						id={`resultatsRecherche.menu.${typeChamp}.${rechercheAvanceeEnum.reference}.title`}
						defaultMessage='Nom de la Résidence ou "RefRésidence-RefLot"'
						description='Label du bloc de recherche par Nom de la Résidence ou "RefRésidence-RefLot"'
					/>
				</Grid>

				{/* Conteneur du champ réference */}
				<Grid container item xs={12}>
					<Grid container item xs={12}>
						<Field
							name={`${typeChamp}.${rechercheAvanceeEnum.reference}`}
							component={FormTextFieldInline}
							id={`${typeChamp}.${rechercheAvanceeEnum.reference}`}
							label={
								intl.formatMessage(
									{
										id: `resultatsRecherche.menu.${typeChamp}.${rechercheAvanceeEnum.reference}.label`,
										defaultMessage: '(exemple : "Dareau" ou "424-C1113")',
										description: 'Label de l\'intitulé ou nom commercial'
									}
								)
							}
							disabled={rechercheAvanceGestionDisabled(`${typeChamp}.${rechercheAvanceeEnum.reference}`)}
							maxLength={255}
						/>
					</Grid>
				</Grid>
			</Grid>

			{/* Bloc "surface minimum" */}
			<Grid container item xs={12} style={espaceEntreBlocs}>
				{/* Titre du champ "surface minimum" */}
				<Grid
					container
					item
					xs={12}
					style={styleLabel(theme)}
				>
					<FormattedMessage
						id={`resultatsRecherche.menu.${typeChamp}.${rechercheAvanceeEnum.surface}.label`}
						defaultMessage="Quelle surface ?"
						description="Label du bloc de définition de la surface minimum"
					/>
				</Grid>

				{/* Conteneur du champ de surface minimum */}
				<Grid container item xs={12} spacing={2}>
					<Grid container item xs={6}>
						<Field
							name={`${typeChamp}.${rechercheAvanceeEnum.surface}.${surfaceEnum.min}`}
							component={FormNumberFormatField}
							id={`${typeChamp}.${rechercheAvanceeEnum.surface}.${surfaceEnum.min}`}
							label={
								intl.formatMessage(
									{
										id: `resultatsRecherche.menu.${typeChamp}.${rechercheAvanceeEnum.surface}.${surfaceEnum.min}.label`,
										defaultMessage: 'Min',
										description: 'Label de la surface minimum'
									}
								)
							}
							disabled={rechercheAvanceGestionDisabled(`${typeChamp}.${rechercheAvanceeEnum.surface}.${surfaceEnum.min}`)}
							maxLength={3}
							allowNegative={false}
							decimalScale={0}
							formType={FormTypeEnum.nombreRecherche}
							endAnorment={
								intl.formatMessage({
									id: 'global.surface',
									defaultMessage: ' m²',
									description: 'Suffix de la surface'
								})
							}
						/>
					</Grid>

					<Grid container item xs={6}>
						<Field
							name={`${typeChamp}.${rechercheAvanceeEnum.surface}.${surfaceEnum.max}`}
							component={FormNumberFormatField}
							id={`${typeChamp}.${rechercheAvanceeEnum.surface}.${surfaceEnum.max}`}
							label={
								intl.formatMessage(
									{
										id: `resultatsRecherche.menu.${typeChamp}.${rechercheAvanceeEnum.surface}.${surfaceEnum.max}.label`,
										defaultMessage: 'Max',
										description: 'Label de la surface maximum'
									}
								)
							}
							disabled={rechercheAvanceGestionDisabled(`${typeChamp}.${rechercheAvanceeEnum.surface}.${surfaceEnum.min}`)}
							maxLength={3}
							allowNegative={false}
							decimalScale={0}
							formType={FormTypeEnum.nombreRecherche}
							endAnorment={
								intl.formatMessage({
									id: 'global.surface',
									defaultMessage: ' m²',
									description: 'Suffix de la surface'
								})
							}
						/>
					</Grid>
				</Grid>
			</Grid>

			{/* Bloc "Combien de chambres" */}
			<Grid container item xs={12} style={espaceEntreBlocs}>
				<Field
					name={`${typeChamp}.${rechercheAvanceeEnum.combienDeChambre}`}
					component={FieldsSelectionMultipleRechercheForm}
					id={`${typeChamp}.${rechercheAvanceeEnum.combienDeChambre}`}
					label={
						intl.formatMessage(
							{
								id: `resultatsRecherche.menu.${typeChamp}.${rechercheAvanceeEnum.combienDeChambre}.label`,
								defaultMessage: 'Combien de chambres ?',
								description: 'Label du champ de sélection du nombre de chambres'
							}
						)
					}
					options={
						objectGetKeys(nombreChambreEnum).map(
							(nombreChambre) => ({
								label: intl.formatMessage({
									id: `resultatsRecherche.menu.${typeChamp}.${rechercheAvanceeEnum.combienDeChambre}.${nombreChambre}`,
									defaultMessage: '1',
									description: 'Label des options du nombre de chambre'
								}),
								value: nombreChambreEnum[nombreChambre as keyof typeof nombreChambreEnum]
							})
						)
					}
				/>
			</Grid>

			{/* Bloc "À quel étage" */}
			<Grid container item xs={12} style={espaceEntreBlocs}>
				<Field
					name={`${typeChamp}.${rechercheAvanceeEnum.quelEtage}`}
					component={FieldsSelectionMultipleRechercheForm}
					id={`${typeChamp}.${rechercheAvanceeEnum.quelEtage}`}
					label={
						intl.formatMessage(
							{
								id: `resultatsRecherche.menu.${typeChamp}.${rechercheAvanceeEnum.quelEtage}.label`,
								defaultMessage: 'À quel étage ?',
								description: 'Label du champ de sélection de l\'étage'
							}
						)
					}
					options={
						objectGetKeys(etageEnum).map(
							(etage) => ({
								label: intl.formatMessage({
									id: `resultatsRecherche.menu.${typeChamp}.${rechercheAvanceeEnum.quelEtage}.${etage}`,
									defaultMessage: 'RDC',
									description: 'Label des options de l\'étage'
								}),
								value: etageEnum[etage as keyof typeof etageEnum]
							})
						)
					}
				/>
			</Grid>

			{/* Bloc "Choisissez vos prestations" */}
			{/* TODO Remettre style={espacePrestations} après traitement de la checkbox dernierEtage */}
			<Grid container item xs={12} style={espaceEntreBlocs}>
				<Field
					name={`${typeChamp}.${rechercheAvanceeEnum.prestation}`}
					component={FieldsSelectionMultipleRechercheForm}
					id={`${typeChamp}.${rechercheAvanceeEnum.prestation}`}
					label={
						intl.formatMessage(
							{
								id: `resultatsRecherche.menu.${typeChamp}.${rechercheAvanceeEnum.prestation}.label`,
								defaultMessage: 'Choisissez vos prestations',
								description: 'Label du champ de sélection des prestations'
							}
						)
					}
					options={
						objectGetKeys(prestationsEnum)
							.sort((a, b) => {
								const valeursPrestation = formValues[champsMenuRechercheEnum.rechercheAvancee][rechercheAvanceeEnum.prestation]
								const valeurA = prestationsEnum[a as keyof typeof prestationsEnum]
								const valeurB = prestationsEnum[b as keyof typeof prestationsEnum]

								if (valeursPrestation.includes(valeurB) && valeursPrestation.includes(valeurA)) {
									return valeursPrestation.indexOf(valeurA) - valeursPrestation.indexOf(valeurB)
								}

								return Number(valeursPrestation.includes(valeurB)) - Number(valeursPrestation.includes(valeurA))
							})
							.slice(0, voirPlusPrestation ? undefined : 5)
							.map(
								(prestation) => ({
									label: (
										<div
											style={{
												display: 'flex',
												flexDirection: 'column',
												alignItems: 'center'
											}}
										>
											{selectionSVG(prestationsEnum[prestation as keyof typeof prestationsEnum])}

											<span>
												<FormattedMessage
													id={`resultatsRecherche.menu.${typeChamp}.${rechercheAvanceeEnum.prestation}.${prestation}`}
													defaultMessage="Balcon"
													description="Label des options de la prestation"
												/>
											</span>
										</div>
									),
									value: prestationsEnum[prestation as keyof typeof prestationsEnum]
								})
							)
					}
				/>

				<Link
					color="colorAccessibility"
					prevent
					onClick={() => setVoirPlusPrestation((prevState) => !prevState)}
					hasChevron={false}
					noAnimation
					textStyle="text8"
					style={{
						textDecoration: 'underline',
						marginTop: 18
					}}
				>
					<FormattedMessage
						id={`resultatsRecherche.menu.${typeChamp}.${rechercheAvanceeEnum.prestation}.${voirPlusPrestation ? 'voirMoins' : 'voirPlus'}`}
						defaultMessage="Afficher plus"
						description="'bouton' pour voir plus"
					/>
				</Link>
			</Grid>

			{/* Bloc "Souhaitez-vous un parking ?" */}
			<Grid container item xs={12} style={espacePrestations}>
				<Field
					name={`${typeChamp}.${rechercheAvanceeEnum.parking}`}
					component={FieldsSelectionMultipleRechercheForm}
					id={`${typeChamp}.${rechercheAvanceeEnum.parking}`}
					label={
						intl.formatMessage(
							{
								id: `resultatsRecherche.menu.${typeChamp}.${rechercheAvanceeEnum.parking}.label`,
								defaultMessage: 'Souhaitez-vous un parking ?',
								description: 'Label du champ de sélection du type de parking'
							}
						)
					}
					options={
						objectGetKeys(parkingEnum)
							.filter((parking) => isNaN(Number(parking)))
							.sort((a, b) => {
								const valeursParking = formValues[champsMenuRechercheEnum.rechercheAvancee][rechercheAvanceeEnum.parking]
								const valeurA = parkingEnum[a as keyof typeof parkingEnum]
								const valeurB = parkingEnum[b as keyof typeof parkingEnum]

								return Number(valeursParking.includes(valeurB)) - Number(valeursParking.includes(valeurA))
							})
							.slice(0, voirPlusParking ? undefined : 5)
							.map(
								(parking) => ({
									label: (
										<div
											style={{
												display: 'flex',
												flexDirection: 'column',
												alignItems: 'center'
											}}
										>
											<FormattedMessage
												id={`resultatsRecherche.menu.${typeChamp}.${rechercheAvanceeEnum.parking}.${parking}`}
												defaultMessage="Box Simple"
												description="Label des options du parking"
											/>
										</div>
									),
									value: parkingEnum[parking as keyof typeof parkingEnum]
								})
							)
					}
				/>

				<Link
					color="colorAccessibility"
					prevent
					onClick={() => setVoirPlusParking((prevState) => !prevState)}
					hasChevron={false}
					noAnimation
					textStyle="text8"
					style={{
						textDecoration: 'underline',
						marginTop: 18
					}}
				>
					<FormattedMessage
						id={`resultatsRecherche.menu.${typeChamp}.${rechercheAvanceeEnum.prestation}.${voirPlusParking ? 'voirMoins' : 'voirPlus'}`}
						defaultMessage="Afficher plus"
						description="'bouton' pour voir plus"
					/>
				</Link>
			</Grid>
			{/* Bloc "PMR" */}
			<Grid container item xs={12}>
				<Grid container item xs={12} style={styleLabel(theme)}>
					<FormattedMessage
						id={`resultatsRecherche.menu.${typeChamp}.${rechercheAvanceeEnum.pmr}.label`}
						defaultMessage="Accessible aux personnes à mobilité réduite (PMR)"
						description="PMR"
					/>
				</Grid>
				<Field
					name={`${typeChamp}.${rechercheAvanceeEnum.pmr}`}
					component={CheckBoxField}
					type="checkbox"
					label={
						intl.formatMessage(
							{
								id: `resultatsRecherche.menu.${typeChamp}.${rechercheAvanceeEnum.pmr}.option`,
								defaultMessage: 'PMR',
								description: 'Checkbox pour le choix des lots PMR'
							}
						)
					}
					textStyle="text2"
				/>
			</Grid>
		</Grid>
	)
}

// Fonction pour gérer dynamiquement l'affichage des icônes selon le type de champ parcouru
export const chargementIcone = (typeChamps: champsMenuRechercheEnum) => {
	switch (typeChamps) {
		case champsMenuRechercheEnum.location:
			return <PlaceOutlinedIcon color="primary" />
		case champsMenuRechercheEnum.typeBien:
			return <HomeOutlinedIcon color="primary" />
		case champsMenuRechercheEnum.budget:
			return <EuroOutlinedIcon color="primary" />
		case champsMenuRechercheEnum.rechercheAvancee:
			return <AddOutlinedIcon color="primary" />
	}
}

export const ChargementTexteChamp = (formValues: FormulaireRechercheModel, typeChamps: champsMenuRechercheEnum) => {
	const theme = useTheme()
	const intl = useIntl()

	// Fonction pour afficher les valeurs sélectionnées dans les formulaires plutôt que le label si défini
	const gestionAffichageLabel = (typeChamps: champsMenuRechercheEnum) => {
		if (typeChamps === champsMenuRechercheEnum.budget) {
			return !!(formValues[champsMenuRechercheEnum.budget] && (formValues[champsMenuRechercheEnum.budget][budgetEnum.min] || formValues[champsMenuRechercheEnum.budget][budgetEnum.max]))
		}

		if (typeChamps === champsMenuRechercheEnum.rechercheAvancee) {
			return !!(formValues[champsMenuRechercheEnum.rechercheAvancee] && (
				formValues[champsMenuRechercheEnum.rechercheAvancee][rechercheAvanceeEnum.combienDeChambre].length > 0 ||
				formValues[champsMenuRechercheEnum.rechercheAvancee][rechercheAvanceeEnum.dernierEtage] ||
				formValues[champsMenuRechercheEnum.rechercheAvancee][rechercheAvanceeEnum.pmr] ||
				formValues[champsMenuRechercheEnum.rechercheAvancee][rechercheAvanceeEnum.parking].length > 0 ||
				formValues[champsMenuRechercheEnum.rechercheAvancee][rechercheAvanceeEnum.quelEtage].length > 0 ||
				formValues[champsMenuRechercheEnum.rechercheAvancee][rechercheAvanceeEnum.prestation].length > 0 || (
					formValues[champsMenuRechercheEnum.rechercheAvancee][rechercheAvanceeEnum.surface] && (
						formValues[champsMenuRechercheEnum.rechercheAvancee][rechercheAvanceeEnum.surface][surfaceEnum.min] ||
						formValues[champsMenuRechercheEnum.rechercheAvancee][rechercheAvanceeEnum.surface][surfaceEnum.max]
					)
				) ||
				formValues[champsMenuRechercheEnum.rechercheAvancee][rechercheAvanceeEnum.reference]
			))
		}

		return (formValues[typeChamps] as string[]).length > 0
	}

	return (
		<div>
			<span
				style={{
					color: gestionAffichageLabel(typeChamps) ? theme.palette.blue.main : '#8895a1'
				}}
				className="champ-label"
			>
				{
					gestionAffichageLabel(typeChamps) ? (
						<>
							{
								// Si on parcourt le budget
								typeChamps === champsMenuRechercheEnum.budget ? (
									<>
										{
											// On check si un montant max est renseigné
											formValues[champsMenuRechercheEnum.budget][budgetEnum.max] && (
												<FormattedMessage
													id={`resultatsRecherche.menu.${typeChamps}.${budgetEnum.max}.formLabel`}
													defaultMessage="{montantMax} € max"
													description="Montant max"
													values={{
														montantMax: formValues[champsMenuRechercheEnum.budget][budgetEnum.max]
													}}
												/>
											)
										}

										{
											// On check si le montant min ET le montant max sont renseignés, on sépare d'une virgule
											formValues[champsMenuRechercheEnum.budget][budgetEnum.min] && formValues[champsMenuRechercheEnum.budget][budgetEnum.max] && (
												<>, </>
											)
										}

										{
											// On check si un montant min est renseigné
											formValues[champsMenuRechercheEnum.budget][budgetEnum.min] && (
												<FormattedMessage
													id={`resultatsRecherche.menu.${typeChamps}.${budgetEnum.min}.formLabel`}
													defaultMessage="{montantMin} € min"
													description="Montant min"
													values={{
														montantMin: formValues[champsMenuRechercheEnum.budget][budgetEnum.min]
													}}
												/>
											)
										}
									</>
								) : typeChamps !== champsMenuRechercheEnum.rechercheAvancee ? (
									<>
										{
											// Le check de gestionAffichageLabel renvoie forcément un string[]
											(formValues[typeChamps] as string[]).map(
												(formValue) => intl.formatMessage({
													id: `resultatsRecherche.menu.${typeChamps}.${formValue}`,
													defaultMessage: 'Paris',
													description: 'Form value label'
												})
											).join(', ')
										}
									</>
								) : (
									<FormattedMessage
										id="resultatsRecherche.menu.rechercheAvancee.placeholder"
										defaultMessage="Recherche avancée"
										description="Item de recherche avancée dans le menu"
									/>
								)
							}
						</>
					) : (
						<FormattedMessage
							id={`resultatsRecherche.menu.${typeChamps}.placeholder`}
							defaultMessage="Localisation"
							description="Item dans le menu de recherche"
						/>
					)
				}
			</span>
		</div>
	)
}